import { useEffect, useMemo, useState } from 'react';
import API from '../services/api';
import {
  Archive,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Download,
  Plus,
  Settings,
  Wallet,
  Trash2,
  XCircle
} from 'lucide-react';
import InvoiceSettings from '../components/InvoiceSettings';
import BulkInvoiceCreator from '../components/BulkInvoiceCreator';
import AccountingWorkbookPanel from '../components/AccountingWorkbookPanel';

function Invoices() {
  const now = new Date();

  const [activeTab, setActiveTab] = useState('overview'); // overview | archive
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedAgent, setSelectedAgent] = useState('');

  const [agents, setAgents] = useState([]);

  const [monthOverviewRows, setMonthOverviewRows] = useState([]);
  const [uninvoicedRows, setUninvoicedRows] = useState([]);
  const [archiveSummaryRows, setArchiveSummaryRows] = useState([]);
  const [recentInvoiceRows, setRecentInvoiceRows] = useState([]);

  const [expandedAgent, setExpandedAgent] = useState(null);
  const [agentDetailsMap, setAgentDetailsMap] = useState({});

  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);

  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingUninvoiced, setLoadingUninvoiced] = useState(false);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [loadingRecentInvoices, setLoadingRecentInvoices] = useState(false);
  const [loadingInvoiceDetails, setLoadingInvoiceDetails] = useState(false);

  const [showSettings, setShowSettings] = useState(false);
  const [showBulkCreator, setShowBulkCreator] = useState(false);

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    if (activeTab === 'overview') {
      loadMonthOverview();
      loadUninvoiced();
    }
    if (activeTab === 'archive') {
      loadArchiveSummary();
      loadRecentInvoices();
    }
    setExpandedAgent(null);
    setAgentDetailsMap({});
  }, [activeTab, year, month, selectedAgent]);

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const { data } = await API.get('/invoicing/agents');
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fehler beim Laden der Agenten:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadMonthOverview = async () => {
    setLoadingOverview(true);
    try {
      const { data } = await API.get('/invoicing/month-overview', {
        params: { year, month }
      });

      let rows = Array.isArray(data?.rows) ? data.rows : [];
      if (selectedAgent) {
        rows = rows.filter((r) => r.agent_name === selectedAgent);
      }

      setMonthOverviewRows(rows);
    } catch (err) {
      console.error('Fehler beim Laden der Monatsübersicht:', err);
      setMonthOverviewRows([]);
    } finally {
      setLoadingOverview(false);
    }
  };

  const loadUninvoiced = async () => {
    setLoadingUninvoiced(true);
    try {
      const params = { year, month };
      if (selectedAgent) params.agent = selectedAgent;

      const { data } = await API.get('/invoicing/uninvoiced', { params });
      setUninvoicedRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error('Fehler beim Laden der noch fakturierbaren Positionen:', err);
      setUninvoicedRows([]);
    } finally {
      setLoadingUninvoiced(false);
    }
  };

  const loadArchiveSummary = async () => {
    setLoadingArchive(true);
    try {
      const params = { year, month };
      if (selectedAgent) params.agent = selectedAgent;

      const { data } = await API.get('/invoicing/archive/month-summary', { params });
      setArchiveSummaryRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error('Fehler beim Laden des Archivs:', err);
      setArchiveSummaryRows([]);
    } finally {
      setLoadingArchive(false);
    }
  };


  const loadRecentInvoices = async () => {
    setLoadingRecentInvoices(true);
    try {
      const { data } = await API.get('/invoicing/archive/recent', {
        params: { limit: 30 }
      });
      setRecentInvoiceRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error('Fehler beim Laden der zuletzt erstellten Rechnungen:', err);
      setRecentInvoiceRows([]);
    } finally {
      setLoadingRecentInvoices(false);
    }
  };

  const loadAgentDetails = async (agentName) => {
    if (expandedAgent === agentName) {
      setExpandedAgent(null);
      return;
    }

    if (!agentDetailsMap[agentName]) {
      try {
        const { data } = await API.get('/invoicing/month-agent-details', {
          params: {
            year,
            month,
            agent: agentName
          }
        });

        setAgentDetailsMap((prev) => ({
          ...prev,
          [agentName]: data
        }));
      } catch (err) {
        console.error('Fehler beim Laden der Agent-Details:', err);
        return;
      }
    }

    setExpandedAgent(agentName);
  };

  const loadInvoiceDetails = async (invoiceNumber) => {
    if (!invoiceNumber) return;

    if (selectedInvoiceNumber === invoiceNumber) {
      setSelectedInvoiceNumber(null);
      setInvoiceDetails(null);
      return;
    }

    setLoadingInvoiceDetails(true);
    try {
      const { data } = await API.get(`/invoicing/archive/${invoiceNumber}/details`);
      setSelectedInvoiceNumber(invoiceNumber);
      setInvoiceDetails(data);
    } catch (err) {
      console.error('Fehler beim Laden der Rechnungsdetails:', err);
      setSelectedInvoiceNumber(null);
      setInvoiceDetails(null);
    } finally {
      setLoadingInvoiceDetails(false);
    }
  };

  const downloadInvoicePdf = async (invoiceNumber, variant = 'monthly') => {
    if (!invoiceNumber) return;

    try {
      const response = await API.get(
        `/invoicing/archive/${encodeURIComponent(invoiceNumber)}/regenerate`,
        {
          params: { variant },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rechnung-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Fehler beim erneuten Erzeugen der Rechnung:', err);
      alert('PDF konnte nicht erneut erzeugt werden: ' + (err.response?.data?.error || err.message));
    }
  };

  const resetInvoice = async (invoiceNumber) => {
    if (!invoiceNumber) return;

    const ok = window.confirm(
      `Rechnung ${invoiceNumber} wirklich zurücksetzen?\n\nDie zugehörigen Positionen werden wieder als noch fakturierbar markiert. Die Rechnungsnummer wird von den Aufträgen entfernt.`
    );

    if (!ok) return;

    try {
      await API.delete(`/invoicing/archive/${encodeURIComponent(invoiceNumber)}`);
      setSelectedInvoiceNumber(null);
      setInvoiceDetails(null);
      await Promise.all([loadArchiveSummary(), loadRecentInvoices(), loadMonthOverview(), loadUninvoiced()]);
      alert(`Rechnung ${invoiceNumber} wurde zurückgesetzt.`);
    } catch (err) {
      console.error('Fehler beim Zurücksetzen der Rechnung:', err);
      alert('Rechnung konnte nicht zurückgesetzt werden: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReloadAfterCreate = async () => {
    if (activeTab === 'overview') {
      await Promise.all([loadMonthOverview(), loadUninvoiced()]);
    } else {
      await Promise.all([loadArchiveSummary(), loadRecentInvoices()]);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(Number(amount || 0));
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('de-DE');
  };

  const stats = useMemo(() => {
    return monthOverviewRows.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.erstattet += Number(row.erstattet || 0);
        acc.storno += Number(row.storno || 0);
        acc.offen += Number(row.offen || 0);
        acc.fakturiert += Number(row.fakturiert || 0);
        acc.noch_fakturierbar += Number(row.noch_fakturierbar || 0);
        return acc;
      },
      {
        total: 0,
        erstattet: 0,
        storno: 0,
        offen: 0,
        fakturiert: 0,
        noch_fakturierbar: 0
      }
    );
  }, [monthOverviewRows]);

  const uninvoicedAmount = useMemo(() => {
    return uninvoicedRows.reduce((sum, row) => sum + Number(row.betrag || 0), 0);
  }, [uninvoicedRows]);

  const monthLabel = `${monthNames[month - 1]} ${year}`;

  const statusBadge = (status) => {
    const base = {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 8px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600
    };

    if (status === 'Offen') {
      return {
        ...base,
        background: 'rgba(245, 158, 11, 0.15)',
        color: '#b45309'
      };
    }

    if (status === 'Rechnungsfähig') {
      return {
        ...base,
        background: 'rgba(59, 130, 246, 0.15)',
        color: '#1d4ed8'
      };
    }

    return {
      ...base,
      background: 'rgba(16, 185, 129, 0.15)',
      color: '#047857'
    };
  };

  const orderStatusBadge = (status) => {
    const base = {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 8px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600
    };

    switch (status) {
      case 'Erstattet':
        return { ...base, background: 'rgba(16,185,129,0.15)', color: '#047857' };
      case 'Storno':
        return { ...base, background: 'rgba(239,68,68,0.15)', color: '#b91c1c' };
      case 'Eingereicht':
      case 'Reviewed':
      case 'Bestellt':
        return { ...base, background: 'rgba(245,158,11,0.15)', color: '#b45309' };
      default:
        return { ...base, background: 'var(--bg2)', color: 'var(--text2)' };
    }
  };

  return (
    <div className="page">
      <div
        style={{
          padding: 24,
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap'
        }}
      >
        <div>
          <h1 style={{ fontSize: 28, marginBottom: 8 }}>Rechnungen</h1>
          <div style={{ color: 'var(--text2)', fontSize: 14 }}>
            Neue Monatsstruktur mit Monatsübersicht, noch fakturierbaren Positionen und Archiv
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('overview')}
              style={{
                padding: '8px 16px',
                background: activeTab === 'overview' ? 'var(--accent)' : 'var(--bg2)',
                color: activeTab === 'overview' ? 'white' : 'var(--text1)',
                border: activeTab === 'overview' ? 'none' : '1px solid var(--border)',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Übersicht
            </button>

            <button
              onClick={() => setActiveTab('archive')}
              style={{
                padding: '8px 16px',
                background: activeTab === 'archive' ? 'var(--accent)' : 'var(--bg2)',
                color: activeTab === 'archive' ? 'white' : 'var(--text1)',
                border: activeTab === 'archive' ? 'none' : '1px solid var(--border)',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              <Archive size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Archiv
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {activeTab === 'overview' && (
            <button
              onClick={() => setShowBulkCreator(true)}
              className="btn"
              style={{ background: 'var(--accent)', color: 'white' }}
            >
              <Plus size={18} style={{ marginRight: 6 }} />
              Rechnungen erstellen
            </button>
          )}

          <button onClick={() => setShowSettings(true)} className="btn btn-ghost">
            <Settings size={18} style={{ marginRight: 6 }} />
            Einstellungen
          </button>
        </div>
      </div>

      <div style={{ padding: 24, borderBottom: '1px solid var(--border)' }}>
        <div
          style={{
            display: 'flex',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => {
                if (month === 1) {
                  setMonth(12);
                  setYear((y) => y - 1);
                } else {
                  setMonth((m) => m - 1);
                }
              }}
              className="btn btn-ghost"
              style={{ padding: 8 }}
            >
              <ChevronLeft size={20} />
            </button>

            <div style={{ minWidth: 180, textAlign: 'center', fontWeight: 700, fontSize: 18 }}>
              {monthLabel}
            </div>

            <button
              onClick={() => {
                if (year === now.getFullYear() && month === now.getMonth() + 1) return;
                if (month === 12) {
                  setMonth(1);
                  setYear((y) => y + 1);
                } else {
                  setMonth((m) => m + 1);
                }
              }}
              className="btn btn-ghost"
              style={{ padding: 8 }}
              disabled={year === now.getFullYear() && month === now.getMonth() + 1}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: 'var(--bg)',
                color: 'var(--text1)',
                minWidth: 220
              }}
            >
              <option value="">Alle Agenten</option>
              {agents.map((agent) => (
                <option key={agent} value={agent}>
                  {agent}
                </option>
              ))}
            </select>

            <div style={{ fontSize: 13, color: 'var(--text2)' }}>
              {loadingAgents ? 'Agenten werden geladen...' : `${agents.length} Agenten verfügbar`}
            </div>
          </div>
        </div>
      </div>

      {activeTab === 'overview' && (
        <>
          <div style={{ padding: 24 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                gap: 16
              }}
            >
              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
                  AGENTEN IM MONAT
                </div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>{monthOverviewRows.length}</div>
              </div>

              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
                  <CheckCircle2 size={14} style={{ color: '#10b981', verticalAlign: 'middle', marginRight: 4 }} />
                  FAKTURIERT
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{stats.fakturiert}</div>
              </div>

              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
                  <AlertCircle size={14} style={{ color: '#3b82f6', verticalAlign: 'middle', marginRight: 4 }} />
                  NOCH FAKTURIERBAR
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#2563eb' }}>
                  {stats.noch_fakturierbar}
                </div>
                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text2)' }}>
                  {formatCurrency(uninvoicedAmount)}
                </div>
              </div>

              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
                  <XCircle size={14} style={{ color: '#f59e0b', verticalAlign: 'middle', marginRight: 4 }} />
                  OFFEN
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#d97706' }}>{stats.offen}</div>
              </div>
            </div>
          </div>

          <div style={{ padding: '0 24px 24px 24px' }}>
            <div className="card" style={{ overflow: 'hidden' }}>
              <div
                style={{
                  padding: 16,
                  borderBottom: '1px solid var(--border)',
                  fontSize: 18,
                  fontWeight: 700
                }}
              >
                Monatsübersicht nach Agent
              </div>

              {loadingOverview ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                  Monatsübersicht wird geladen...
                </div>
              ) : monthOverviewRows.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                  Keine Daten für {monthLabel}
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>AGENT</th>
                      <th style={{ textAlign: 'right' }}>TOTAL</th>
                      <th style={{ textAlign: 'right' }}>ERSTATTET</th>
                      <th style={{ textAlign: 'right' }}>FAKTURIERT</th>
                      <th style={{ textAlign: 'right' }}>NOCH FAKTURIERBAR</th>
                      <th style={{ textAlign: 'right' }}>OFFEN</th>
                      <th style={{ textAlign: 'right' }}>STORNO</th>
                      <th>STATUS</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthOverviewRows.map((row) => {
                      const isExpanded = expandedAgent === row.agent_name;
                      const details = agentDetailsMap[row.agent_name];

                      return (
                        <>
                          <tr
                            key={row.agent_name}
                            className="hover-row"
                            style={{ cursor: 'pointer' }}
                            onClick={() => loadAgentDetails(row.agent_name)}
                          >
                            <td style={{ fontWeight: 700 }}>{row.agent_name}</td>
                            <td style={{ textAlign: 'right' }}>{row.total}</td>
                            <td style={{ textAlign: 'right' }}>{row.erstattet}</td>
                            <td style={{ textAlign: 'right', color: '#10b981', fontWeight: 700 }}>
                              {row.fakturiert}
                            </td>
                            <td style={{ textAlign: 'right', color: '#2563eb', fontWeight: 700 }}>
                              {row.noch_fakturierbar}
                            </td>
                            <td style={{ textAlign: 'right', color: '#d97706', fontWeight: 700 }}>
                              {row.offen}
                            </td>
                            <td style={{ textAlign: 'right' }}>{row.storno}</td>
                            <td>
                              <span style={statusBadge(row.monat_status)}>{row.monat_status}</span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <ChevronDown
                                size={18}
                                style={{
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s'
                                }}
                              />
                            </td>
                          </tr>

                          {isExpanded && details && (
                            <tr>
                              <td colSpan={9} style={{ padding: 0, background: 'var(--bg2)' }}>
                                <div style={{ padding: 16 }}>
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                      gap: 12,
                                      marginBottom: 16
                                    }}
                                  >
                                    <div className="card" style={{ padding: 12 }}>
                                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>TOTAL</div>
                                      <div style={{ fontSize: 22, fontWeight: 700 }}>{details.total}</div>
                                    </div>
                                    <div className="card" style={{ padding: 12 }}>
                                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>ERSTATTET</div>
                                      <div style={{ fontSize: 22, fontWeight: 700 }}>{details.erstattet}</div>
                                    </div>
                                    <div className="card" style={{ padding: 12 }}>
                                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>FAKTURIERT</div>
                                      <div style={{ fontSize: 22, fontWeight: 700 }}>{details.fakturiert}</div>
                                    </div>
                                    <div className="card" style={{ padding: 12 }}>
                                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>NOCH FAKTURIERBAR</div>
                                      <div style={{ fontSize: 22, fontWeight: 700 }}>
                                        {details.noch_fakturierbar}
                                      </div>
                                    </div>
                                    <div className="card" style={{ padding: 12 }}>
                                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>OFFEN</div>
                                      <div style={{ fontSize: 22, fontWeight: 700 }}>{details.offen}</div>
                                    </div>
                                  </div>

                                  <div style={{ overflowX: 'auto' }}>
                                    <table className="table" style={{ fontSize: 13 }}>
                                      <thead>
                                        <tr>
                                          <th>Datum</th>
                                          <th>Bestellung</th>
                                          <th>Account</th>
                                          <th>Produkt</th>
                                          <th style={{ textAlign: 'right' }}>Betrag</th>
                                          <th>Status</th>
                                          <th>Rechnung</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {details.orders.map((order) => (
                                          <tr key={order.id}>
                                            <td>{formatDate(order.order_date)}</td>
                                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                              {order.order_number}
                                            </td>
                                            <td>{order.account_name || '-'}</td>
                                            <td
                                              style={{
                                                maxWidth: 320,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                              }}
                                              title={order.product_name}
                                            >
                                              {order.product_name}
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                              {formatCurrency(order.betrag)}
                                            </td>
                                            <td>
                                              <span style={orderStatusBadge(order.status)}>{order.status}</span>
                                            </td>
                                            <td>
                                              {order.invoice_number ? (
                                                <span
                                                  style={{
                                                    fontFamily: 'monospace',
                                                    fontSize: 12,
                                                    color: 'var(--accent)',
                                                    fontWeight: 700
                                                  }}
                                                >
                                                  {order.invoice_number}
                                                </span>
                                              ) : (
                                                '-'
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div style={{ padding: '0 24px 24px 24px' }}>
            <div className="card" style={{ overflow: 'hidden' }}>
              <div
                style={{
                  padding: 16,
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 700 }}>Noch fakturierbar</div>
                <div style={{ color: 'var(--text2)', fontSize: 14 }}>
                  {uninvoicedRows.length} Positionen · {formatCurrency(uninvoicedAmount)}
                </div>
              </div>

              {loadingUninvoiced ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                  Positionen werden geladen...
                </div>
              ) : uninvoicedRows.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                  Keine noch fakturierbaren Positionen für {monthLabel}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Agent</th>
                        <th>Bestellung</th>
                        <th>Account</th>
                        <th>Produkt</th>
                        <th style={{ textAlign: 'right' }}>Betrag</th>
                        <th>Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uninvoicedRows.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDate(row.order_date)}</td>
                          <td style={{ fontWeight: 700 }}>{row.agent_name}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.order_number}</td>
                          <td>{row.account_name || '-'}</td>
                          <td
                            style={{
                              maxWidth: 320,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            title={row.product_name}
                          >
                            {row.product_name}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>
                            {formatCurrency(row.betrag)}
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {row.payment_id || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'archive' && (
        <div style={{ padding: 24 }}>

          <div className="card" style={{ overflow: 'hidden', marginBottom: 24 }}>
            <div
              style={{
                padding: 16,
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap'
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>Zuletzt erstellte Rechnungen</div>
                <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>
                  Neueste Rechnungen unabhängig von Monat oder Agent
                </div>
              </div>
              <button onClick={loadRecentInvoices} className="btn btn-ghost">
                Aktualisieren
              </button>
            </div>

            {loadingRecentInvoices ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                Zuletzt erstellte Rechnungen werden geladen...
              </div>
            ) : recentInvoiceRows.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                Keine archivierten Rechnungen gefunden
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Rechnung</th>
                      <th>Agent</th>
                      <th>Monat</th>
                      <th style={{ textAlign: 'right' }}>Positionen</th>
                      <th style={{ textAlign: 'right' }}>Summe</th>
                      <th>Erstellt / geändert</th>
                      <th style={{ textAlign: 'right' }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentInvoiceRows.map((row) => (
                      <tr key={`${row.invoice_number}-${row.agent_name}`}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{row.invoice_number}</td>
                        <td style={{ fontWeight: 700 }}>{row.agent_name}</td>
                        <td>{monthNames[row.month - 1]} {row.year}</td>
                        <td style={{ textAlign: 'right' }}>{row.order_count}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(row.total_amount)}</td>
                        <td>{formatDate(row.created_at)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => loadInvoiceDetails(row.invoice_number)}
                              className="btn btn-ghost"
                              style={{ padding: '5px 9px', fontSize: 12 }}
                              title="Details laden"
                            >
                              <Eye size={13} />
                            </button>
                            <button
                              onClick={() => downloadInvoicePdf(row.invoice_number, 'monthly')}
                              className="btn btn-ghost"
                              style={{ padding: '5px 9px', fontSize: 12 }}
                              title="PDF erneut erzeugen"
                            >
                              <Download size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>


          <div className="card" style={{ overflow: 'hidden' }}>
            <div
              style={{
                padding: 16,
                borderBottom: '1px solid var(--border)',
                fontSize: 18,
                fontWeight: 700
              }}
            >
              Archiv nach Agent und Monat
            </div>

            {loadingArchive ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                Archiv wird geladen...
              </div>
            ) : archiveSummaryRows.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                Keine archivierten Rechnungen für {monthLabel}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Monat</th>
                      <th style={{ textAlign: 'right' }}>Rechnungen</th>
                      <th style={{ textAlign: 'right' }}>Positionen</th>
                      <th style={{ textAlign: 'right' }}>Summe</th>
                      <th>Zeitraum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archiveSummaryRows.map((row, index) => (
                      <tr key={`${row.agent_name}-${row.year}-${row.month}-${index}`}>
                        <td style={{ fontWeight: 700 }}>{row.agent_name}</td>
                        <td>
                          {monthNames[row.month - 1]} {row.year}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700 }}>{row.invoice_count}</div>
                          {Array.isArray(row.invoice_numbers) && row.invoice_numbers.length > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: 4,
                                marginTop: 6
                              }}
                            >
                              {row.invoice_numbers.map((invoiceNumber) => (
                                <button
                                  key={invoiceNumber}
                                  onClick={() => loadInvoiceDetails(invoiceNumber)}
                                  className="btn btn-ghost"
                                  style={{
                                    padding: '3px 7px',
                                    fontSize: 11,
                                    fontFamily: 'monospace'
                                  }}
                                  title="Rechnungsdetails laden"
                                >
                                  {invoiceNumber}
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>{row.order_count}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>
                          {formatCurrency(row.total_amount)}
                        </td>
                        <td>
                          {formatDate(row.first_order_date)} – {formatDate(row.last_order_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ height: 24 }} />

          <div className="card" style={{ overflow: 'hidden' }}>
            <div
              style={{
                padding: 16,
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap'
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700 }}>Rechnungsdetails</div>
              <div style={{ color: 'var(--text2)', fontSize: 14 }}>
                Wähle eine Rechnung aus einer bekannten Rechnungsnummer
              </div>
            </div>

            <div style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  value={selectedInvoiceNumber || ''}
                  onChange={(e) => setSelectedInvoiceNumber(e.target.value)}
                  placeholder="Rechnungsnummer eingeben, z. B. 20250247"
                  style={{
                    padding: '10px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    background: 'var(--bg)',
                    color: 'var(--text1)',
                    minWidth: 320
                  }}
                />
                <button
                  onClick={() => loadInvoiceDetails(selectedInvoiceNumber)}
                  className="btn"
                  style={{ background: 'var(--accent)', color: 'white' }}
                >
                  <Eye size={16} style={{ marginRight: 6 }} />
                  Laden
                </button>
              </div>
            </div>

            {loadingInvoiceDetails ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                Rechnungsdetails werden geladen...
              </div>
            ) : !invoiceDetails ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                Noch keine Rechnung geladen
              </div>
            ) : (
              <>
                <div style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 16
                    }}
                  >
                    <div className="card" style={{ padding: 14 }}>
                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>RECHNUNG</div>
                      <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'monospace' }}>
                        {invoiceDetails.invoice_number}
                      </div>
                    </div>
                    <div className="card" style={{ padding: 14 }}>
                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>AGENT</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{invoiceDetails.agent_name}</div>
                    </div>
                    <div className="card" style={{ padding: 14 }}>
                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>POSITIONEN</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{invoiceDetails.order_count}</div>
                    </div>
                    <div className="card" style={{ padding: 14 }}>
                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>SUMME</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>
                        {formatCurrency(invoiceDetails.total_amount)}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                    <button
                      onClick={() => downloadInvoicePdf(invoiceDetails.invoice_number, 'monthly')}
                      className="btn"
                      style={{ background: 'var(--accent)', color: 'white' }}
                    >
                      <Download size={16} style={{ marginRight: 6 }} />
                      PDF erneut erzeugen
                    </button>

                    <button
                      onClick={() => downloadInvoicePdf(invoiceDetails.invoice_number, 'detailed')}
                      className="btn btn-ghost"
                    >
                      <FileText size={16} style={{ marginRight: 6 }} />
                      Detail-PDF
                    </button>

                    <button
                      onClick={() => downloadInvoicePdf(invoiceDetails.invoice_number, 'transactions')}
                      className="btn btn-ghost"
                    >
                      <Wallet size={16} style={{ marginRight: 6 }} />
                      Zahlungsdetails-PDF
                    </button>

                    <button
                      onClick={() => resetInvoice(invoiceDetails.invoice_number)}
                      className="btn btn-ghost"
                      style={{ color: '#b91c1c', borderColor: 'rgba(185,28,28,0.35)' }}
                    >
                      <Trash2 size={16} style={{ marginRight: 6 }} />
                      Rechnung zurücksetzen
                    </button>
                  </div>

                  <div style={{ marginTop: 12, color: 'var(--text2)', fontSize: 14 }}>
                    Leistungszeitraum: {formatDate(invoiceDetails.first_order_date)} – {formatDate(invoiceDetails.last_order_date)}
                  </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Bestellung</th>
                        <th>Account</th>
                        <th>Produkt</th>
                        <th style={{ textAlign: 'right' }}>Betrag</th>
                        <th>Status</th>
                        <th>Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceDetails.orders.map((order) => (
                        <tr key={order.id}>
                          <td>{formatDate(order.order_date)}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{order.order_number}</td>
                          <td>{order.account_name || '-'}</td>
                          <td
                            style={{
                              maxWidth: 320,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            title={order.product_name}
                          >
                            {order.product_name}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>
                            {formatCurrency(order.betrag)}
                          </td>
                          <td>
                            <span style={orderStatusBadge(order.status)}>{order.status}</span>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {order.payment_id || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showSettings && (
        <InvoiceSettings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showBulkCreator && (
        <BulkInvoiceCreator
          isOpen={showBulkCreator}
          onClose={() => setShowBulkCreator(false)}
          onSuccess={handleReloadAfterCreate}
        />
      )}
    </div>
  );
}

function AccountingAwareInvoices() {
  const [accountingProfile, setAccountingProfile] = useState(null);
  const [accountingProfileLoaded, setAccountingProfileLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    API.get('/accounting/profile')
      .then(({ data }) => { if (mounted) setAccountingProfile(data || null); })
      .catch((err) => {
        console.warn('Accounting-Profil konnte nicht geladen werden; klassische Rechnungsansicht bleibt aktiv.', err);
      })
      .finally(() => { if (mounted) setAccountingProfileLoaded(true); });
    return () => { mounted = false; };
  }, []);

  if (!accountingProfileLoaded) {
    return <div className="page" style={{ padding: 24, color: 'var(--text2)' }}>Rechnungsbereich wird geladen…</div>;
  }

  if (accountingProfile?.mode === 'payment_date_workbook') {
    return <AccountingWorkbookPanel />;
  }

  return <Invoices />;
}

export default AccountingAwareInvoices;